import { useState, useEffect } from 'react';
import { useLocation, useSearch } from 'wouter';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { SavedGame } from '@/types/game';
import { STORAGE_KEYS } from '@/lib/constants';
import ConfirmDialog from '@/components/ConfirmDialog';

function HistoryView() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const gameId = new URLSearchParams(search).get('id');
  
  const [games, setGames] = useState<SavedGame[]>([]);
  const [selectedGame, setSelectedGame] = useState<SavedGame | null>(null);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadGames();
  }, []);

  useEffect(() => {
    if (gameId && games.length > 0) {
      const game = games.find(g => g.id === gameId);
      setSelectedGame(game || null);
    } else {
      setSelectedGame(null);
    }
  }, [gameId, games]);

  const loadGames = () => {
    try {
      const historyJson = localStorage.getItem(STORAGE_KEYS.GAME_HISTORY);
      const history: SavedGame[] = historyJson ? JSON.parse(historyJson) : [];
      setGames(history);
    } catch (err) {
      console.error('Failed to load game history:', err);
      toast.error('Failed to load game history');
      setGames([]);
    }
  };

  const handleClearAll = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.GAME_HISTORY, JSON.stringify([]));
      setGames([]);
      setShowClearAllDialog(false);
      toast.success('All games cleared');
      setLocation('/history');
    } catch (err) {
      console.error('Failed to clear history:', err);
      toast.error('Failed to clear history');
    }
  };

  const handleDeleteGame = () => {
    if (!selectedGame) return;
    
    try {
      const updated = games.filter(g => g.id !== selectedGame.id);
      localStorage.setItem(STORAGE_KEYS.GAME_HISTORY, JSON.stringify(updated));
      setGames(updated);
      setShowDeleteDialog(false);
      toast.success('Game deleted');
      setLocation('/history');
    } catch (err) {
      console.error('Failed to delete game:', err);
      toast.error('Failed to delete game');
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Detail view for a specific game
  if (selectedGame) {
    const yellowCards = selectedGame.cards.filter(c => c.type === 'yellow');
    const redCards = selectedGame.cards.filter(c => c.type === 'red');

    return (
      <div className="min-h-[100dvh] p-4">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setLocation('/history')}
              className="bg-on-dark-10 border-on-dark-20 text-on-dark hover:bg-on-dark-20"
              aria-label="Back to history"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold text-on-dark" style={{ fontFamily: 'Teko, "Teko Fallback", sans-serif' }}>
              GAME DETAILS
            </h1>
          </div>

          <Card className="p-6 bg-card space-y-6">
            {/* Date & Time */}
            <div>
              <div className="text-2xl font-bold">
                {formatDate(selectedGame.timestamp)}
              </div>
              <div className="text-sm text-muted-foreground">
                {formatTime(selectedGame.timestamp)}
              </div>
            </div>

            {/* Age Group */}
            <div>
              <div className="text-sm text-muted-foreground mb-1">Age Group</div>
              <Badge variant="secondary" className="text-lg px-3 py-1">
                {selectedGame.settings.ageGroup}
              </Badge>
            </div>

            {/* Final Score */}
            <div>
              <div className="text-sm text-muted-foreground mb-2">Final Score</div>
              <div className="flex items-center justify-center gap-8 py-4">
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-2 drop-shadow-md"
                    style={{ backgroundColor: selectedGame.settings.homeTeamColor }}
                  >
                    {selectedGame.finalScore.home}
                  </div>
                  <div className="font-semibold">{selectedGame.settings.homeTeamName}</div>
                </div>
                <div className="text-2xl font-bold text-muted-foreground">vs</div>
                <div className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white mb-2 drop-shadow-md"
                    style={{ backgroundColor: selectedGame.settings.awayTeamColor }}
                  >
                    {selectedGame.finalScore.away}
                  </div>
                  <div className="font-semibold">{selectedGame.settings.awayTeamName}</div>
                </div>
              </div>
            </div>

            {/* Cards */}
            {selectedGame.cards.length > 0 && (
              <div>
                <div className="text-sm text-muted-foreground mb-2">Cards Issued</div>
                <div className="space-y-2">
                  {yellowCards.length > 0 && (
                    <div>
                      <div className="font-semibold mb-1 flex items-center gap-2">
                        <div className="w-4 h-6 bg-yellow-400 rounded-sm"></div>
                        Yellow Cards ({yellowCards.length})
                      </div>
                      <div className="pl-6 space-y-2">
                        {yellowCards.map(card => (
                          <div key={card.id} className="text-sm">
                            <div>
                              {card.team === 'home' ? selectedGame.settings.homeTeamName : selectedGame.settings.awayTeamName} #{card.playerNumber}
                              <span className="text-muted-foreground ml-2">
                                (Half {card.half})
                              </span>
                            </div>
                            {card.reason && (
                              <div className="text-xs text-muted-foreground mt-0.5 pl-2 border-l-2 border-yellow-400">
                                {card.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {redCards.length > 0 && (
                    <div>
                      <div className="font-semibold mb-1 flex items-center gap-2">
                        <div className="w-4 h-6 bg-red-500 rounded-sm"></div>
                        Red Cards ({redCards.length})
                      </div>
                      <div className="pl-6 space-y-2">
                        {redCards.map(card => (
                          <div key={card.id} className="text-sm">
                            <div>
                              {card.team === 'home' ? selectedGame.settings.homeTeamName : selectedGame.settings.awayTeamName} #{card.playerNumber}
                              <span className="text-muted-foreground ml-2">
                                (Half {card.half})
                              </span>
                            </div>
                            {card.reason && (
                              <div className="text-xs text-muted-foreground mt-0.5 pl-2 border-l-2 border-red-500">
                                {card.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedGame.notes && (
              <div>
                <div className="text-sm text-muted-foreground mb-2">Game Notes</div>
                <div className="bg-muted p-3 rounded-md whitespace-pre-wrap">
                  {selectedGame.notes}
                </div>
              </div>
            )}

            {/* Game Settings */}
            <div>
              <div className="text-sm text-muted-foreground mb-2">Game Settings</div>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div>Half Duration: {selectedGame.settings.halfDuration} minutes</div>
                <div>Number of Halves: {selectedGame.settings.numberOfHalves}</div>
              </div>
            </div>

            {/* Delete Button */}
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
              className="w-full"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Game
            </Button>
          </Card>
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDeleteGame}
          title="Delete Game"
          description="Are you sure you want to delete this game record? This action cannot be undone."
          confirmText="Delete"
        />
      </div>
    );
  }

  // List view
  return (
    <div className="min-h-[100dvh] p-4">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setLocation('/')}
            className="bg-on-dark-10 border-on-dark-20 text-on-dark hover:bg-on-dark-20"
            aria-label="Back to game"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold text-on-dark flex-1" style={{ fontFamily: 'Teko, "Teko Fallback", sans-serif' }}>
            GAME HISTORY
          </h1>
        </div>

        {games.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No games saved yet</p>
            <p className="text-sm text-muted-foreground mt-2">
              Games will be automatically saved when the final half ends
            </p>
          </Card>
        ) : (
          <>
            {/* Clear All Button */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowClearAllDialog(true)}
                className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Clear All
              </Button>
            </div>

            {/* Games List */}
            <div className="space-y-2">
              {games.map(game => (
                <Card
                  key={game.id}
                  className="p-4 hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => setLocation(`/history?id=${game.id}`)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-semibold">
                        {formatDate(game.timestamp)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1 truncate">
                        <Badge variant="secondary" className="mr-2">
                          {game.settings.ageGroup}
                        </Badge>
                        {game.settings.homeTeamName} {game.finalScore.home} - {game.finalScore.away} {game.settings.awayTeamName}
                      </div>
                      {game.cards.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {game.cards.filter(c => c.type === 'yellow').length} yellow, {game.cards.filter(c => c.type === 'red').length} red
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Clear All Confirmation Dialog */}
      <ConfirmDialog
        open={showClearAllDialog}
        onOpenChange={setShowClearAllDialog}
        onConfirm={handleClearAll}
        title="Clear All Games"
        description="Are you sure you want to delete all game records? This action cannot be undone."
        confirmText="Clear All"
      />
    </div>
  );
}

export default HistoryView;